import moment from "moment";
import { STATUS_ICON_TYPE, TIME_REPORT_STATUS } from "../configs/constants/TimeReportStatus";
import TimeReport, { Suggestion, TimeReportMetadata, User } from "../models/TimeReport";
import { AbsenceType } from "../configs/constants/AbsencesType";

export const getHistoryArray = (status: TIME_REPORT_STATUS, metadata: TimeReportMetadata) => {
	interface History {
		icon?: STATUS_ICON_TYPE;
		changedAt?: Date;
		changedBy?: User;
		label?: string;
		nextStatus?: TIME_REPORT_STATUS;
	}

	const {
		reportedAt,
		reportedBy,
		adminAttestedAt,
		adminAttestedBy,
		customerAttestedAt,
		customerAttestedBy,
		customerDeclinedAt,
		customerDeclinedBy,
		resolvedDeclineAt,
		resolvedDeclineBy,
		invoicedAt,
		invoicedBy,
		silentAttestedAt,
	} = metadata;

	let history2!: History; //3rd block can be change with customer action, admin resolve or silent attest

	switch (status) {
		case TIME_REPORT_STATUS.ATTESTED_BY_CUSTOMER:
			history2 = {
				icon: "DONE",
				changedAt: customerAttestedAt,
				changedBy: customerAttestedBy,
				label: "CUSTOMER_ATTEST",
			};
			break;

		case TIME_REPORT_STATUS.DECLINED_BY_CUSTOMER:
			history2 = {
				icon: "DENIED",
				changedAt: customerDeclinedAt,
				changedBy: customerDeclinedBy,
				label: "CUSTOMER_ATTEST",
				nextStatus: TIME_REPORT_STATUS.RESOLVED_DECLINE_BY_ADMIN,
			};
			break;

		case TIME_REPORT_STATUS.SILENT_ATTESTED:
			history2 = {
				icon: "DONE",
				changedAt: silentAttestedAt,
				label: "SILENT_ATTESTED",
			};
			break;

		case TIME_REPORT_STATUS.RESOLVED_DECLINE_BY_ADMIN:
			history2 = {
				icon: "RESOLVED",
				changedAt: resolvedDeclineAt,
				changedBy: resolvedDeclineBy,
				label: "CUSTOMER_ATTEST",
			};
			break;
	}

	let history: History[] = [];

	switch (status) {
		case TIME_REPORT_STATUS.PENDING:
			history = [];
			break;

		case TIME_REPORT_STATUS.WORKED:
		case TIME_REPORT_STATUS.NOT_WORKED:
			history = [{ icon: "DONE", changedAt: reportedAt, changedBy: reportedBy as User }];
			break;

		case TIME_REPORT_STATUS.ATTESTED_BY_ADMIN:
			history = [
				{ icon: "DONE", changedAt: reportedAt, changedBy: reportedBy as User },
				{ icon: "DONE", changedAt: adminAttestedAt, changedBy: adminAttestedBy as User },
			];
			break;

		case TIME_REPORT_STATUS.ATTESTED_BY_CUSTOMER:
			history = [
				{ icon: "DONE", changedAt: reportedAt, changedBy: reportedBy as User },
				{ icon: "DONE", changedAt: adminAttestedAt, changedBy: adminAttestedBy as User },
				history2,
			];
			break;

		case TIME_REPORT_STATUS.DECLINED_BY_CUSTOMER:
			history = [
				{ icon: "DONE", changedAt: reportedAt, changedBy: reportedBy as User },
				{ icon: "DONE", changedAt: adminAttestedAt, changedBy: adminAttestedBy as User },
				history2,
			];
			break;

		case TIME_REPORT_STATUS.SILENT_ATTESTED:
			history = [
				{ icon: "DONE", changedAt: reportedAt, changedBy: reportedBy as User },
				{ icon: "DONE", changedAt: adminAttestedAt, changedBy: adminAttestedBy as User },
				history2,
			];
			break;

		case TIME_REPORT_STATUS.RESOLVED_DECLINE_BY_ADMIN:
			history = [
				{ icon: "DONE", changedAt: reportedAt, changedBy: reportedBy as User },
				{ icon: "DONE", changedAt: adminAttestedAt, changedBy: adminAttestedBy as User },
				history2,
			];
			break;

		case TIME_REPORT_STATUS.INVOICED:
			history = [
				{ icon: "DONE", changedAt: reportedAt, changedBy: reportedBy as User },
				{ icon: "DONE", changedAt: adminAttestedAt, changedBy: adminAttestedBy as User },
				history2,
				{ icon: "DONE", changedAt: invoicedAt, changedBy: invoicedBy as User },
			];
			break;
	}

	return history;
};

export const convertDecimalToTime = (decimalHours: number): string => {
	const hours: number = Math.floor(decimalHours);
	const minutes: number = Math.round((decimalHours - hours) * 60);
	return minutes && hours ? `${hours} h ${minutes} min` : hours ? `${hours} h` : `${minutes} min`;
};

export const makeListUnique = (list: Suggestion[]): Suggestion[] => {
	const uniqueList: Suggestion[] = [];
	const valueSet: Set<string> = new Set();

	for (const item of list) {
		if (!valueSet.has(item.value)) {
			uniqueList.push(item);
			valueSet.add(item.value);
		}
	}

	return uniqueList;
};

export const sortData = (
	sort: keyof TimeReport,
	order: string
): ((a: TimeReport, b: TimeReport) => number) => {
	return (a: TimeReport, b: TimeReport) => {
		const valueA = deepGet(a, sort);
		const valueB = deepGet(b, sort);

		let comparison = 0;

		if (valueA > valueB) {
			comparison = 1;
		} else if (valueA < valueB) {
			comparison = -1;
		}

		return order === "0" ? comparison : comparison * parseInt(order, 10);
	};
};

export const deepGet = (obj: object, path: string): any => {
	const keys = path.split(".");

	for (let key of keys) {
		if (obj && obj.hasOwnProperty(key)) {
			obj = obj[key as keyof typeof obj];
		} else {
			return undefined;
		}
	}

	return obj;
};

/**
 * CLIENT-SIDE status filtering for time reports.
 *
 * This function implements sophisticated filtering logic that CANNOT be replicated
 * in backend MongoDB queries without complex aggregation pipelines. This is why
 * we load all time reports for a date range and filter them in-memory.
 *
 * Key differences from simple status matching:
 * - FUTURE_TIME_REPORT: Distinguishes pending reports based on shift.end vs current time
 * - PENDING: Only shows pending reports where shift has already ended
 * - INVOICED: Checks invoiceBatch field existence, not status field
 * - SICK_LEAVE/CHILD_CARE: Filters by absence.type field
 *
 * @param report - The time report to check
 * @param statusList - Array of status filter keys to match against
 * @returns true if the report matches any of the filters in statusList
 */
export const getFilteredReportItem = (report: TimeReport, statusList?: string[]): boolean => {
	if (!statusList || statusList.length === 0) return false;

	// Absence type filters - check absence.type field
	if (statusList.includes("SICK_LEAVE") && report.absence?.type === AbsenceType.SICK_LEAVE) {
		return true;
	}
	if (statusList.includes("CHILD_CARE") && report.absence?.type === AbsenceType.CHILD_CARE) {
		return true;
	}

	// Time-based filtering: FUTURE_TIME_REPORT (pending + shift not ended yet)
	if (
		statusList.includes("FUTURE_TIME_REPORT") &&
		report.status === "PENDING" &&
		moment(report.shift.end).isAfter(moment()) &&
		!report.absence
	) {
		return true;
	}

	// Time-based filtering: PENDING (pending + shift already ended)
	if (
		statusList.includes("PENDING") &&
		report.status === "PENDING" &&
		moment(report.shift.end).isBefore(moment()) &&
		!report.absence
	) {
		return true;
	}

	// Direct status matching filters
	if (statusList.includes("ATTESTED_BY_ADMIN") && report.status === "ATTESTED_BY_ADMIN") {
		return true;
	}
	if (statusList.includes("ATTESTED_BY_CUSTOMER") && report.status === "ATTESTED_BY_CUSTOMER") {
		return true;
	}
	if (statusList.includes("SILENT_ATTESTED") && report.status === "SILENT_ATTESTED") {
		return true;
	}
	if (statusList.includes("DECLINED_BY_CUSTOMER") && report.status === "DECLINED_BY_CUSTOMER") {
		return true;
	}

	// Compound condition: INVOICED checks invoiceBatch field, not status
	if (statusList.includes("INVOICED") && report.invoiceBatch) {
		return true;
	}

	if (statusList.includes("WORKED") && report.status === "WORKED") {
		return true;
	}

	return false;
};

export interface TimeReportFilterOptions {
	start?: Date;
	end?: Date;
	consultant?: string;
	customer?: string;
	job?: string;
	statusList?: string[];
	pool?: string;
	salaryBatch?: string;
	invoiceBatch?: string;
	searchTerm?: string;
}

/**
 * Client-side filter + sort over already-loaded time reports (was
 * TimeReportStore.getTimeReportsWithFilter). All filtering happens here,
 * including the sophisticated status filtering via getFilteredReportItem()
 * that cannot be expressed in backend queries.
 */
export function filterAndSortTimeReports(
	timeReports: TimeReport[],
	{
		start,
		end,
		consultant,
		customer,
		job,
		statusList = [],
		sort,
		order,
		pool,
		salaryBatch,
		invoiceBatch,
		searchTerm,
	}: TimeReportFilterOptions & { sort: keyof TimeReport; order: string }
): TimeReport[] {
	return timeReports
		.filter((tr) => {
			const dateOk = !start || !end || moment(tr.shift.start).isBetween(start, end, "day", "[]");
			const consultantOk = !consultant || tr.consultant?.id === consultant;
			const customerOk = !customer || tr.job?.customer?.id === customer;
			const jobOk = !job || tr.job?.id === job;
			const statusListOk = statusList?.length !== 0 && getFilteredReportItem(tr, statusList);
			const poolOk = !pool || tr.job?.pool === pool;
			const salaryBatchOk = !salaryBatch || tr.salaryBatch?.id === salaryBatch;
			const invoiceBatchOk = !invoiceBatch || tr.invoiceBatch?.id === invoiceBatch;

			// Search term filtering - search across multiple fields
			let searchOk = true;
			if (searchTerm) {
				const lowerSearchTerm = searchTerm.toLowerCase();
				const searchableText = [
					tr.consultant?.firstName,
					tr.consultant?.lastName,
					tr.job?.customer?.name,
					tr.job?.title,
					tr.job?.orderNumber?.toString(),
					tr.status,
				]
					.filter(Boolean)
					.join(" ")
					.toLowerCase();
				searchOk = searchableText.includes(lowerSearchTerm);
			}

			return (
				dateOk &&
				consultantOk &&
				customerOk &&
				statusListOk &&
				jobOk &&
				poolOk &&
				salaryBatchOk &&
				invoiceBatchOk &&
				searchOk
			);
		})
		.sort(sortData(sort, order));
}

/**
 * Status breakdown counts for the filter popover (was
 * TimeReportStore.getStatusCounts): count per status respecting all other
 * active filters, but ignoring the status filter itself.
 */
export function getTimeReportStatusCounts(
	timeReports: TimeReport[],
	{ statusOptions, ...filters }: TimeReportFilterOptions & { statusOptions: string[] }
): Record<string, number> {
	const counts: Record<string, number> = {};

	statusOptions.forEach((statusKey) => {
		counts[statusKey] = filterAndSortTimeReports(timeReports, {
			...filters,
			statusList: [statusKey],
			sort: "shift" as keyof TimeReport,
			order: "asc",
		}).length;
	});

	return counts;
}
